import { useState, useEffect } from 'react';

/**
 * Hook to manage user enrollment in interview series
 * This is a temporary solution until API endpoints are implemented
 */
export const useEnrollmentManager = () => {
  // State to track all enrolled series
  const [enrolledSeries, setEnrolledSeries] = useState<string[]>([]);
  
  // Load enrolled series from localStorage on mount
  useEffect(() => {
    const storedEnrolled = localStorage.getItem('enrolled_series');
    if (storedEnrolled) {
      try {
        const parsed = JSON.parse(storedEnrolled);
        if (Array.isArray(parsed)) {
          setEnrolledSeries(parsed);
        }
      } catch (e) {
        console.error('Error parsing enrolled series from localStorage:', e);
      }
    }
  }, []);
  
  /**
   * Check if user is enrolled in a specific series
   * @param seriesId Series ID to check enrollment for
   * @returns boolean indicating if user is enrolled
   */
  const isEnrolled = (seriesId: string): boolean => {
    return enrolledSeries.includes(seriesId);
  };
  
  /**
   * Enroll user in a specific series
   * @param seriesId Series ID to enroll in
   * @returns boolean indicating success
   */
  const enrollInSeries = (seriesId: string): boolean => {
    if (isEnrolled(seriesId)) return true; // Already enrolled
    
    try {
      // Update local state
      const updatedEnrolled = [...enrolledSeries, seriesId];
      setEnrolledSeries(updatedEnrolled);
      
      // Persist to localStorage
      localStorage.setItem('enrolled_series', JSON.stringify(updatedEnrolled));
      return true;
    } catch (e) {
      console.error('Error enrolling in series:', e);
      return false;
    }
  };
  
  /**
   * Unenroll user from a specific series
   * @param seriesId Series ID to unenroll from
   * @returns boolean indicating success
   */
  const unenrollFromSeries = (seriesId: string): boolean => {
    if (!isEnrolled(seriesId)) return true; // Not enrolled
    
    try {
      // Update local state
      const updatedEnrolled = enrolledSeries.filter(id => id !== seriesId);
      setEnrolledSeries(updatedEnrolled);
      
      // Persist to localStorage
      localStorage.setItem('enrolled_series', JSON.stringify(updatedEnrolled));
      return true;
    } catch (e) {
      console.error('Error unenrolling from series:', e);
      return false;
    }
  };
  
  return {
    isEnrolled,
    enrollInSeries,
    unenrollFromSeries,
    enrolledSeries
  };
};

export default useEnrollmentManager; 